import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, Loader2, XCircle, Package, Mail, Mic, ImageIcon, X, Heart, Send, Video } from 'lucide-react';
import { useCart } from '../context/CartContext';
import supabase from '../supabaseClient';

const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart } = useCart();
  const sessionId = searchParams.get('session_id');
  const isLtl = location.pathname.startsWith('/letters-that-last');

  const [status, setStatus] = useState('polling');
  const [order, setOrder] = useState(null);
  const [attempts, setAttempts] = useState(0);

  // Standalone Memory Vault purchases must route into the vault editor, not the
  // generic LTL-style upload form. Detected from the order items so it works for
  // both the live Stripe flow and the £0 TEST100 free-order path.
  const isStandaloneVault = !!(
    order?.items?.length &&
    order.items.every((i) => String(i.id) === 'memory-vault-standalone')
  );

  // Upload state
  const [uploadExpanded, setUploadExpanded] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [messageText, setMessageText] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoError, setVideoError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploaded, setUploaded] = useState(false);

  const clearCartRef = useRef(clearCart);
  clearCartRef.current = clearCart;

  const pollStatus = useCallback(async () => {
    if (!sessionId) { setStatus('failed'); return; }
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
      const data = await res.json().catch(() => ({}));

      if (data.payment_status === 'paid') {
        setOrder(data);
        setStatus('success');
        clearCartRef.current();
        return;
      }
      if (!res.ok || data.status === 'expired') {
        if (attempts >= 8) setStatus('failed');
        else setAttempts(prev => prev + 1);
        return;
      }
      if (attempts < 8) setAttempts(prev => prev + 1);
      else setStatus('failed');
    } catch {
      if (attempts < 8) setAttempts(prev => prev + 1);
      else setStatus('failed');
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    if (status !== 'polling') return;
    const timer = setTimeout(pollStatus, attempts === 0 ? 500 : 2000);
    return () => clearTimeout(timer);
  }, [status, attempts, pollStatus]);

  // LTL post-payment flow: no auto-redirect (Prompt 36).
  // Customers get an explicit "Add your memory now" CTA + reassurance
  // that they can return via the emailed link. Matches the Memory Vault
  // pattern where the customer chooses when to begin.

  // Standalone Memory Vault orders: NO auto-redirect.
  // Customer clicks the explicit "Begin your Memory Vault" CTA when ready.

  const handleVideoAdd = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setVideoError('');
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > 30) {
        setVideoError('Video must be 30 seconds or less.');
        setVideoFile(null);
      } else {
        setVideoFile(file);
      }
    };
    video.onerror = () => { setVideoFile(file); };
    video.src = URL.createObjectURL(file);
    e.target.value = '';
  };

  const handleImageAdd = (e) => {
    const files = Array.from(e.target.files);
    const allowed = 3 - imageFiles.length;
    if (allowed <= 0) return;
    setImageFiles(prev => [...prev, ...files.slice(0, allowed)]);
    e.target.value = '';
  };

  const removeImage = (idx) => setImageFiles(prev => prev.filter((_, i) => i !== idx));

  // Task 13 — direct-to-Supabase upload removed. Customer media is now
  // written exclusively through the backend's /api/memory/{qr}/upload
  // endpoint (service-role key, private buckets, signed URLs). The old
  // uploadFileToSupabase helper is retained as a tiny no-op shim just in
  // case any other render-path reaches it; it should not be called.
  const uploadFileToSupabase = async () => {
    throw new Error('Direct Supabase Storage upload disabled — route through /api/memory/{qr}/upload');
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!order?.upload_code) return;
    if (!recipientName.trim() && !messageText.trim() && !audioFile && imageFiles.length === 0 && !videoFile) return;

    setSubmitting(true);
    try {
      // Task 13 — route customer-media writes through the backend. The
      // browser no longer needs Supabase Storage write permission; the
      // server uses its service-role key and stores paths that
      // _mint_signed_media_url turns into short-lived signed URLs at read.
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const uploadCode = order.upload_code;
      const form = new FormData();
      if (recipientName.trim()) form.append('recipient_name', recipientName.trim());
      if (messageText.trim()) form.append('message_text', messageText.trim());
      if (audioFile) form.append('audio', audioFile, audioFile.name);
      if (videoFile) form.append('video', videoFile, videoFile.name);
      if (imageFiles.length > 0) {
        imageFiles.forEach((img) => form.append('images', img, img.name));
      }

      const res = await fetch(`${API_URL}/api/memory/${encodeURIComponent(uploadCode)}/upload`, {
        method: 'POST',
        body: form,
      });

      if (res.status === 403) {
        // Row is already locked server-side — treat as already-uploaded, mirroring old UX.
        setUploaded(true);
        return;
      }
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `Upload failed (${res.status})`);
      }

      setUploaded(true);
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Polling state ──
  if (status === 'polling') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center" data-testid="order-polling">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center px-6">
          <Loader2 className="mx-auto mb-6 text-[#C6A96B] animate-spin" size={36} strokeWidth={1.5} />
          <h1 className="font-heading text-2xl sm:text-3xl text-[#0B1F3A] font-light mb-3">Processing your order</h1>
          <p className="text-sm text-[#6B6259]">Please wait while we confirm your payment...</p>
        </motion.div>
      </div>
    );
  }

  // ── Failed state ──
  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center" data-testid="order-failed">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center px-6 max-w-md">
          <div className="w-16 h-16 rounded-full bg-[#C6A96B]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-[#C6A96B]" size={28} strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#0B1F3A] font-light mb-3">Thank You</h1>
          <p className="text-sm text-[#6B6259] mb-4">Your order has been placed. A confirmation email is on its way with everything you need.</p>
          <p className="text-xs text-[#6B6259]/70 mb-8">If you don't see it within a few minutes, please check your spam folder or contact <a href="mailto:hello@sent2ment.co.uk" className="text-[#C6A96B]">hello@sent2ment.co.uk</a></p>
        </motion.div>
      </div>
    );
  }

  // ── Success state ──
  const orderNumber = order?.order_id?.slice(0, 8).toUpperCase() || '';

  return (
    <div className="min-h-screen bg-[#F7F5F2] pt-28 pb-20" data-testid="order-success">
      <div className="max-w-xl mx-auto px-6">

        {/* ── Confirmation Header ── */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-10"
        >
          <div className="w-16 h-16 rounded-full bg-[#C6A96B]/10 flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="text-[#C6A96B]" size={28} strokeWidth={1.5} />
          </div>
          <p className="text-xs tracking-[0.35em] uppercase text-[#C6A96B] mb-4 font-medium" data-testid="order-confirmed-label">Order Confirmed</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#0B1F3A] font-light mb-4" data-testid="order-success-heading">
            Thank You
          </h1>
          <p className="text-sm text-[#6B6259] leading-relaxed max-w-md mx-auto">
            Your order has been placed with care. We'll begin preparing your keepsake with the same thought you put into choosing it.
          </p>
          <p className="text-xs sm:text-sm italic mt-4" style={{ color: '#8A7F73', fontFamily: 'Georgia, serif' }} data-testid="order-success-my-orders-line">
            <a href="/my-orders" className="transition-colors duration-300 hover:text-[#C6A96B]" style={{ color: '#C6A96B', textDecoration: 'none' }} data-testid="order-success-my-orders-link">
              View your order and private space &rarr;
            </a>
          </p>
        </motion.div>

        {/* ── Order Details Card ── */}
        {order && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="bg-white border border-[#C6A96B]/10 p-6 mb-6"
          >
            <div className="flex items-center gap-2 mb-5">
              <Package className="text-[#C6A96B]" size={16} strokeWidth={1.5} />
              <h2 className="text-xs tracking-[0.2em] uppercase text-[#0B1F3A] font-medium">Order Details</h2>
            </div>
            <div className="text-xs text-[#6B6259] mb-4" data-testid="order-id">
              Order: #{orderNumber}
            </div>
            <div className="space-y-3 mb-5">
              {order.items?.map((item, i) => (
                <div key={i} className="flex justify-between items-center" data-testid={`order-item-${i}`}>
                  <div>
                    <p className="text-sm text-[#0B1F3A]">{item.name}</p>
                    <p className="text-[10px] text-[#6B6259]">Qty: {item.quantity}</p>
                  </div>
                  <p className="text-sm text-[#0B1F3A]">£{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#C6A96B]/10 pt-4 flex justify-between">
              <span className="text-sm font-medium text-[#0B1F3A]">Total</span>
              <span className="text-lg font-light font-heading text-[#0B1F3A]" data-testid="order-total">
                £{(order.amount_total ?? 0).toFixed(2)}
              </span>
            </div>
          </motion.div>
        )}

        {/* ── Email Confirmation Note ── */}
        {order?.email && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="bg-white border border-[#C6A96B]/10 p-6 mb-8"
          >
            <div className="flex items-center gap-2 mb-3">
              <Mail className="text-[#C6A96B]" size={16} strokeWidth={1.5} />
              <h2 className="text-xs tracking-[0.2em] uppercase text-[#0B1F3A] font-medium">Confirmation Sent</h2>
            </div>
            <p className="text-sm text-[#6B6259]" data-testid="order-email">
              A confirmation has been sent to <span className="text-[#0B1F3A]">{order.email}</span>
            </p>
          </motion.div>
        )}

        {/* ── LTL: explicit "Add your memory" CTA (Prompt 36 — no auto-redirect;
             customer chooses when to begin, or uses the emailed link later).
             Guarded against Memory Vault standalone AND multi-code LTL bundles. ── */}
        {isLtl && order?.upload_code && !uploaded && !isStandaloneVault &&
          !(Array.isArray(order?.upload_codes) && order.upload_codes.length > 1) && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="bg-[#C6A96B]/5 border border-[#C6A96B]/15 p-7 sm:p-8 mb-8 text-center"
            data-testid="ltl-add-memory-card"
          >
            <Heart className="text-[#C6A96B] mx-auto mb-4" size={22} strokeWidth={1.5} />
            <p className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#C6A96B] mb-3">
              Your keepsake is ordered
            </p>
            <h3 className="font-heading text-xl sm:text-2xl text-[#0B1F3A] font-light mb-3 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Add your memory whenever it feels right.
            </h3>
            <p className="text-xs sm:text-sm text-[#6B6259] leading-relaxed mb-7 max-w-md mx-auto">
              Record a voice message, add a photo, or write something from the heart &mdash; there&rsquo;s no rush.
            </p>
            <a
              href={`/letters-that-last/upload/${order.upload_code}`}
              data-testid="ltl-add-memory-cta"
              className="inline-block bg-[#C6A96B] text-white font-semibold tracking-[0.22em] uppercase text-xs px-12 py-4 transition-all duration-300 hover:bg-[#b89a5e] hover:shadow-[0_8px_28px_rgba(198,169,107,0.35)]"
            >
              Add your memory now
            </a>
            <p className="text-[11px] italic text-[#6B6259]/75 mt-5" style={{ fontFamily: 'Georgia, serif' }} data-testid="ltl-add-memory-later-note">
              Or return later using the secure link we&rsquo;ve just emailed you.
            </p>
          </motion.div>
        )}

        {/* ── Memory Vault: explicit "Begin" CTA (no auto-redirect — customer chooses when to start).
             Multi-purchase: render one independent CTA per vault unit purchased so each
             vault is treated as a separate, fully isolated experience. ── */}
        {isStandaloneVault && order?.upload_code && (() => {
          const vaultCodes = (Array.isArray(order?.upload_codes) && order.upload_codes.length > 0)
            ? order.upload_codes
            : [order.upload_code];
          const isMulti = vaultCodes.length > 1;
          return (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="bg-[#C6A96B]/5 border border-[#C6A96B]/15 p-7 sm:p-8 mb-8 text-center"
              data-testid="vault-redirect-notice"
            >
              <Heart className="text-[#C6A96B] mx-auto mb-4" size={22} strokeWidth={1.5} />
              <p className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#C6A96B] mb-3">
                {isMulti ? `Your ${vaultCodes.length} Memory Vaults are ready` : 'Your Memory Vault is ready'}
              </p>
              <h3 className="font-heading text-xl sm:text-2xl text-[#0B1F3A] font-light mb-3 italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {isMulti ? 'Independent spaces, each yours to fill.' : 'A private space, gently prepared for you to fill.'}
              </h3>
              <p className="text-xs sm:text-sm text-[#6B6259] leading-relaxed mb-7 max-w-md mx-auto">
                {isMulti
                  ? 'Each vault is a separate, private experience — its own passcode, voice, words, and recipient. Begin one whenever it feels right; the others wait quietly until you return.'
                  : 'Add a voice note, a pocket keepsake, three affirmations and four hidden journal moments — at your own pace. You can come back anytime.'}
              </p>
              <div className={`flex flex-col gap-3 ${isMulti ? 'sm:max-w-sm' : 'sm:inline-block'} mx-auto`} data-testid="vault-begin-list">
                {vaultCodes.map((code, idx) => (
                  <a
                    key={code}
                    href={`/vault-edit/${code}`}
                    data-testid={isMulti ? `vault-begin-cta-${idx + 1}` : 'vault-begin-cta'}
                    className="inline-block bg-[#C6A96B] text-white font-semibold tracking-[0.22em] uppercase text-xs px-12 py-4 transition-all duration-300 hover:bg-[#b89a5e] hover:shadow-[0_8px_28px_rgba(198,169,107,0.35)]"
                  >
                    {isMulti
                      ? (idx === 0 ? 'Open your first space' : 'Continue your next space')
                      : 'Open your space'}
                  </a>
                ))}
              </div>
              <p className="text-[11px] italic text-[#6B6259]/75 mt-5" style={{ fontFamily: 'Georgia, serif' }}>
                {isMulti
                  ? 'We\u2019ve also sent every link to your email — return to any of them whenever feels right.'
                  : 'We\u2019ve also sent this link to your email — you can return whenever feels right.'}
              </p>
            </motion.div>
          );
        })()}

        {/* ── Divider + Create Your Memory Section ──
             Hidden for LTL single-letter orders because the Prompt 36 CTA card
             above ("YOUR KEEPSAKE IS ORDERED") is now the primary — and only —
             upload entry point for that path. Preserved for multi-code LTL
             bundles where the inline form is still the primary flow. Never
             shown for Memory Vault standalone. */}
        {(() => {
          const isLtlSingle = isLtl && order?.upload_code && !isStandaloneVault &&
            !(Array.isArray(order?.upload_codes) && order.upload_codes.length > 1);
          if (isLtlSingle) return null;
          return (
            <>
              {/* Divider */}
              <div className="flex items-center gap-4 my-10">
                <div className="flex-1 h-px bg-[#C6A96B]/15"></div>
                <Heart className="text-[#C6A96B]/30" size={16} strokeWidth={1.5} />
                <div className="flex-1 h-px bg-[#C6A96B]/15"></div>
              </div>
              {/* Create Your Memory Section (multi-code LTL only — MV standalone excluded) */}
              {order?.upload_code && !uploaded && !isStandaloneVault && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            data-testid="upload-section"
          >
            <div className="text-center mb-8">
              <p className="text-xs tracking-[0.3em] uppercase text-[#C6A96B] mb-3 font-medium">Next Step</p>
              <h2 className="font-heading text-2xl sm:text-3xl text-[#0B1F3A] font-light mb-3" data-testid="upload-section-heading">
                Create Your Memory
              </h2>
              <p className="text-sm text-[#6B6259] leading-relaxed max-w-md mx-auto">
                Add a personal voice message, photo, or written note — something they'll treasure every time they scan their keepsake.
              </p>
            </div>

            {!uploadExpanded ? (
              <div className="text-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setUploadExpanded(true)}
                  data-testid="start-upload-btn"
                  className="bg-[#C6A96B] text-[#0B1F3A] hover:bg-[#D4B87A] transition-all duration-500 font-semibold tracking-[0.2em] uppercase text-xs px-10 py-4 rounded shadow-[0_4px_20px_rgba(198,169,107,0.2)]"
                >
                  Create Now
                </motion.button>
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <form onSubmit={handleUploadSubmit} className="bg-white border border-[#C6A96B]/10 p-6 space-y-6" data-testid="upload-form">
                  {/* Recipient Name */}
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Recipient's Name</label>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={e => setRecipientName(e.target.value)}
                      placeholder="Who is this for?"
                      data-testid="upload-recipient-input"
                      className="w-full bg-[#F7F5F2] border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors"
                    />
                  </div>

                  {/* Written Message */}
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Your Message</label>
                    <textarea
                      value={messageText}
                      onChange={e => setMessageText(e.target.value)}
                      placeholder="Write something from the heart..."
                      rows={4}
                      data-testid="upload-message-input"
                      className="w-full bg-[#F7F5F2] border border-[#C6A96B]/15 focus:border-[#C6A96B]/40 outline-none px-4 py-3 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/55 transition-colors resize-none leading-relaxed"
                    />
                  </div>

                  {/* Voice Message */}
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">Voice Message</label>
                    {audioFile ? (
                      <div className="flex items-center justify-between bg-[#F7F5F2] border border-[#C6A96B]/15 px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Mic className="text-[#C6A96B] shrink-0" size={16} strokeWidth={1.5} />
                          <span className="text-sm text-[#0B1F3A] truncate">{audioFile.name}</span>
                        </div>
                        <button type="button" onClick={() => setAudioFile(null)} className="text-[#6B6259] hover:text-[#0B1F3A] transition-colors" data-testid="upload-remove-audio">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label htmlFor="confirmation-audio-input" className="block cursor-pointer">
                        <div className="border border-dashed border-[#C6A96B]/20 hover:border-[#C6A96B]/40 px-4 py-5 text-center transition-colors duration-300" data-testid="upload-audio-zone">
                          <Mic className="text-[#C6A96B] mx-auto mb-2" size={18} strokeWidth={1.5} />
                          <p className="text-xs text-[#6B6259]">Upload a voice message</p>
                        </div>
                        <input id="confirmation-audio-input" type="file" accept="audio/*" className="hidden" data-testid="upload-audio-input" onChange={e => { if (e.target.files[0]) setAudioFile(e.target.files[0]); }} />
                      </label>
                    )}
                  </div>

                  {/* Video Message */}
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">
                      Video Message <span className="normal-case tracking-normal text-[#6B6259]/70">(30 seconds max)</span>
                    </label>
                    {videoFile ? (
                      <div className="flex items-center justify-between bg-[#F7F5F2] border border-[#C6A96B]/15 px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <Video className="text-[#C6A96B] shrink-0" size={16} strokeWidth={1.5} />
                          <span className="text-sm text-[#0B1F3A] truncate">{videoFile.name}</span>
                        </div>
                        <button type="button" onClick={() => { setVideoFile(null); setVideoError(''); }} className="text-[#6B6259] hover:text-[#0B1F3A] transition-colors" data-testid="upload-remove-video">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <label htmlFor="confirmation-video-input" className="block cursor-pointer">
                        <div className="border border-dashed border-[#C6A96B]/20 hover:border-[#C6A96B]/40 px-4 py-5 text-center transition-colors duration-300" data-testid="upload-video-zone">
                          <Video className="text-[#C6A96B] mx-auto mb-2" size={18} strokeWidth={1.5} />
                          <p className="text-xs text-[#6B6259]">Upload a short video</p>
                        </div>
                        <input id="confirmation-video-input" type="file" accept="video/*" className="hidden" data-testid="upload-video-input" onChange={handleVideoAdd} />
                      </label>
                    )}
                    {videoError && (
                      <p className="text-xs text-red-400 mt-2" data-testid="upload-video-error">{videoError}</p>
                    )}
                  </div>

                  {/* Photos */}
                  <div>
                    <label className="block text-xs tracking-[0.15em] uppercase text-[#6B6259] mb-2">
                      Photos <span className="normal-case tracking-normal text-[#6B6259]/70">(up to 3)</span>
                    </label>
                    <div className="space-y-2">
                      <AnimatePresence>
                        {imageFiles.map((f, i) => (
                          <motion.div key={f.name + i} initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="flex items-center justify-between bg-[#F7F5F2] border border-[#C6A96B]/15 px-4 py-3">
                            <div className="flex items-center gap-3 min-w-0">
                              <ImageIcon className="text-[#C6A96B] shrink-0" size={16} strokeWidth={1.5} />
                              <span className="text-sm text-[#0B1F3A] truncate">{f.name}</span>
                            </div>
                            <button type="button" onClick={() => removeImage(i)} className="text-[#6B6259] hover:text-[#0B1F3A] transition-colors" data-testid={`upload-remove-image-${i}`}>
                              <X size={16} />
                            </button>
                          </motion.div>
                        ))}
                      </AnimatePresence>
                      {imageFiles.length < 3 && (
                        <label htmlFor="confirmation-image-input" className="block cursor-pointer">
                          <div className="border border-dashed border-[#C6A96B]/20 hover:border-[#C6A96B]/40 px-4 py-5 text-center transition-colors duration-300" data-testid="upload-image-zone">
                            <ImageIcon className="text-[#C6A96B] mx-auto mb-2" size={18} strokeWidth={1.5} />
                            <p className="text-xs text-[#6B6259]">Upload a photo</p>
                          </div>
                          <input id="confirmation-image-input" type="file" accept="image/*" multiple className="hidden" data-testid="upload-image-input" onChange={handleImageAdd} />
                        </label>
                      )}
                    </div>
                  </div>

                  {/* Submit */}
                  <motion.button
                    type="submit"
                    disabled={submitting}
                    whileHover={submitting ? {} : { scale: 1.01 }}
                    whileTap={submitting ? {} : { scale: 0.99 }}
                    data-testid="upload-save-btn"
                    className="w-full bg-[#C6A96B] text-white font-medium tracking-[0.2em] uppercase text-xs py-4 transition-all duration-300 hover:bg-[#b89a5e] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <><Loader2 className="animate-spin" size={14} /> Saving...</>
                    ) : (
                      <><Send size={14} /> Save This Memory</>
                    )}
                  </motion.button>
                </form>
              </motion.div>
            )}

            {/* Reassurance */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-8 text-center"
              data-testid="upload-reassurance"
            >
              <p className="text-sm text-[#6B6259] leading-[1.8] italic font-heading max-w-md mx-auto">
                If you're not ready yet, don't worry. We've sent you a secure link by email so you can upload your message anytime.
              </p>
            </motion.div>
          </motion.div>
        )}
            </>
          );
        })()}

        {/* ── Upload Success ── */}
        {uploaded && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="text-center py-8"
            data-testid="upload-success"
          >
            <div className="w-14 h-14 rounded-full bg-[#C6A96B]/10 flex items-center justify-center mx-auto mb-6">
              <Heart className="text-[#C6A96B]" size={24} strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#0B1F3A] font-light mb-4">Memory Saved</h2>
            <p className="text-sm text-[#6B6259] leading-[1.8] max-w-md mx-auto">
              Your memory has been saved. When the QR code is scanned, your words and moments will be there — just as you left them.
            </p>
          </motion.div>
        )}

        {/* ── Return ── */}
        <div className="text-center mt-12">
          <button
            onClick={() => navigate('/')}
            data-testid="order-home-btn"
            className="text-sm tracking-[0.15em] uppercase text-[#C6A96B] hover:text-[#b89a5e] transition-colors"
          >
            Return to Sent2ment
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
